import { Input } from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import { UserListSorting, getSortedUserLabel } from '@mnspro-cloud/services';
import { Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarUser } from '../Avatar';
import { Button } from '../Button';
import { CommunicationMessage, IllustrationSize, Illustrations } from '../Communication';
import { Flex } from '../Flex';
import { ListSelectionType } from '../List/List.interface';
import { VirtualizedList } from '../List/VirtualizedList';
import { Loader } from '../Loader';
import { ResponsiveDialog } from '../ResponsiveDialog/ResponsiveDialog';
import { ThemeMode, useProviderTheme } from '../hooks/useProviderTheme';
import './PersonPicker.css';

export interface PersonPickerProps {
  open: boolean;
  persons: Person[];
  onPersonSelected: (persons: Person[]) => void;
  onCancelClick: () => void;
  isLoading?: boolean;
  allowMultiselect?: boolean;
  sortMode?: UserListSorting;
}

/**
 * Dialog for selecting a single person record from a list of persons.
 */
export const PersonPicker: React.FC<PersonPickerProps> = ({
  open,
  persons,
  onPersonSelected,
  onCancelClick,
  isLoading = false,
  allowMultiselect = false,
  sortMode,
}) => {
  const { t } = useTranslation();
  const theme = useProviderTheme();
  const [filterQuery, setFilterQuery] = useState<string>('');
  const filterQueryLc = filterQuery.toLocaleLowerCase();
  const [selectedPersons, setSelectedPersons] = useState<Person[]>([]);

  /**
   * Reset filter on each dialog open/close
   */
  useEffect(() => {
    setFilterQuery('');
  }, [open]);

  const filteredPersons = useMemo(
    () =>
      persons.filter(
        (person) =>
          filterQueryLc === '' ||
          person.firstName?.toLowerCase().includes(filterQueryLc) ||
          person.lastName?.toLowerCase().includes(filterQueryLc) ||
          person.displayName?.toLowerCase().includes(filterQueryLc) ||
          person.mailAddress?.toLowerCase().includes(filterQueryLc),
      ),
    [persons, filterQueryLc],
  );

  const listItems = useMemo(
    () =>
      filteredPersons.map((person) => ({
        label: getSortedUserLabel(person, sortMode ?? UserListSorting.FirstName),
        content: person.mailAddress,
        avatar: (
          <LazyAvatarUser
            name={getSortedUserLabel(person, sortMode ?? UserListSorting.FirstName)}
            userId={person.id}
          />
        ),
      })),
    [filteredPersons, sortMode],
  );

  const handleOnSaveClick = () => {
    if (selectedPersons !== null) {
      onPersonSelected(selectedPersons);
    }
  };

  const handleFilterQueryChange = (searchQuery: string) => {
    setFilterQuery(searchQuery);
  };

  const handleSelectionChange = (selected: number[]) => {
    const selectedPersons = selected.map((index) => filteredPersons[index]);
    setSelectedPersons(selectedPersons);
  };

  const selectedIndizes = useMemo(() => {
    const items = selectedPersons
      .map<number>((item) => filteredPersons.findIndex((a) => a.id === item.id))
      .filter((number) => number !== -1) as number[];
    return items;
  }, [selectedPersons, filteredPersons]);

  return (
    <ResponsiveDialog
      open={open}
      className="personPicker"
      header={t('personpicker_header', { defaultValue: 'Person auswählen' })}
      primaryAction={
        <Button
          appearance="primary"
          disabled={selectedPersons.length === 0}
          onClick={handleOnSaveClick}
        >
          {t('person_picker_select_btn_label', { defaultValue: 'Person auswählen' })}
        </Button>
      }
      secondaryAction={
        <Button onClick={onCancelClick}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
      }
    >
      <Flex direction="column" align="stretch" fill gap="0.5rem" className="wrapper">
        <Input
          appearance="filled-darker"
          contentBefore={<Search20Regular />}
          placeholder={t('personpicker_search_placeholder', { defaultValue: 'Personen filtern' })}
          onChange={(_, data) => (data ? handleFilterQueryChange(data?.value) : null)}
        />
        {!isLoading && persons.length > 0 && (
          <>
            {listItems.length > 0 && (
              <VirtualizedList
                entries={listItems}
                className="list"
                selectionType={
                  allowMultiselect ? ListSelectionType.Multi : ListSelectionType.Single
                }
                selectedEntries={selectedIndizes}
                onSelectionChange={handleSelectionChange}
              />
            )}
            {listItems.length === 0 && (
              <div className="list">
                <CommunicationMessage
                  illustration={Illustrations.Empty}
                  illustrationSize={IllustrationSize.Small}
                  headline={t('person_picker_empty_filter_hint', {
                    defaultValue: 'Keine Personen für den angegebenen Suchbegriff gefunden',
                  })}
                  text=""
                  useDarkIllustration={theme.mode === ThemeMode.Dark}
                />
              </div>
            )}
          </>
        )}
        {!isLoading && persons.length === 0 && (
          <div className="list">
            <CommunicationMessage
              illustration={Illustrations.Empty}
              illustrationSize={IllustrationSize.Small}
              headline={t('person_picker_empty_list_hint', {
                defaultValue: 'Keine Personen vorhanden',
              })}
              text=""
              useDarkIllustration={theme.mode === ThemeMode.Dark}
            />
          </div>
        )}
        {isLoading && (
          <Flex className="list" align="center" justify="center">
            <Loader />
          </Flex>
        )}
      </Flex>
    </ResponsiveDialog>
  );
};
